import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@cnx/common";
import { useTranslation } from "react-i18next";
import { showError } from "utils/index";
import DiscussionParticipantService from "services/DiscussionParticipantService";
import { useEffect, useRef } from "react";

export const useDiscussionParticipants = ({
  discussionId,
}: {
  discussionId: string;
}) => {
  const { t } = useTranslation("discussions");
  const errorShownRef = useRef<string | null>(null);

  const { data, isLoading, isSuccess, isError, error } = useQuery({
    queryKey: [QUERY_KEYS.DISCUSSION_PARTICIPANTS, discussionId],
    enabled: Boolean(discussionId),
    queryFn: () => {
      return DiscussionParticipantService.fetchDiscussionParticipantsById({
        discussionId,
      });
    },
  });

  useEffect(() => {
    if (isError) {
      showError(t("participantFetchFailed"));
      return;
    }
  }, [isError, t]);

  useEffect(() => {
    if (
      isSuccess &&
      data &&
      data.length === 1 &&
      errorShownRef.current !== discussionId
    ) {
      showError(t("onlyOneParticipant"));
      errorShownRef.current = discussionId;
    }
  }, [isSuccess, data, t, discussionId]);

  return { participants: data ?? [], isLoading, isSuccess, isError, error };
};

export const useRemoveParticipant = ({
  discussionId,
  userId,
  onSuccess,
}: {
  discussionId: string;
  userId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("discussions");

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      return await DiscussionParticipantService.removeDiscussionParticipant({
        discussionId,
        userId,
      });
    },
    onSuccess: async () => {
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.DISCUSSIONS, discussionId],
      });
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.DISCUSSION_PARTICIPANTS, discussionId],
      });
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.DISCUSSION_CONTEXT, discussionId],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DISCUSSIONS],
      });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      showError(t("removeParticipantFailed"));
    },
  });

  return { mutate, isPending };
};
